import { Line } from 'react-chartjs-2';
import Paper from '@trackman/web-shared-components/components/Paper/Paper';
import { ChartData, ChartOptions } from 'chart.js';
import { NoData } from 'pages/Admin/Analytics/components/NoData/NoData';
import { SourceBadge } from 'pages/Admin/Analytics/components/SourceBadge/SourceBadge';
import { currencyFormatter, formatMonth } from 'pages/Admin/Analytics/utils/monthlySeries';
import { useGetMonthlyRecurringRevenue } from 'queryHooks/Admin/analytics';

const buildChartOptions = (): ChartOptions<'line'> => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: { boxWidth: 10, font: { size: 11 } },
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const value = Number(context.raw ?? 0);
          return context.dataset.yAxisID === 'growth'
            ? `${context.dataset.label}: ${value.toFixed(1)}%`
            : `${context.dataset.label}: ${currencyFormatter.format(value)}`;
        },
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 11 } },
    },
    arr: {
      position: 'left',
      grid: { color: 'rgba(0, 0, 0, 0.06)' },
      ticks: {
        font: { size: 11 },
        callback: (value) => currencyFormatter.format(Number(value)),
      },
    },
    growth: {
      position: 'right',
      grid: { display: false },
      ticks: {
        font: { size: 11 },
        callback: (value) => `${Number(value)}%`,
      },
    },
  },
});

const months = 6;

export const ArrGrowthChart = () => {
  const { data, isLoading, isError } = useGetMonthlyRecurringRevenue(months + 1);

  const hasData = Boolean(data?.length);
  const trimmed = data?.slice(-months) ?? [];

  const growthByIndex = new Map<number, number | null>();
  data?.forEach((entry, index) => {
    if (index === 0) return;
    const previous = data[index - 1];
    growthByIndex.set(index, previous.arr > 0 ? ((entry.arr - previous.arr) / previous.arr) * 100 : null);
  });

  const startIndex = (data?.length ?? 0) - trimmed.length;

  const chartData: ChartData<'line'> = {
    labels: trimmed.map((entry) => formatMonth(entry.year, entry.month)),
    datasets: [
      {
        label: 'ARR',
        data: trimmed.map((entry) => entry.arr),
        borderColor: '#475569',
        backgroundColor: 'rgba(71, 85, 105, 0.15)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        yAxisID: 'arr',
      },
      {
        label: 'MoM growth %',
        data: trimmed.map((_, i) => growthByIndex.get(startIndex + i) ?? null),
        borderColor: '#3B82F6',
        borderWidth: 2,
        tension: 0.4,
        borderDash: [4, 3],
        yAxisID: 'growth',
      },
    ],
  };

  return (
    <Paper className='analytics-card analytics-chart-card'>
      <div className='analytics-card__title-group'>
        <span className='card-title'>ARR growth</span>
        <SourceBadge source='RECURLY' />
      </div>
      <p className='analytics-card__subtitle'>Annual recurring revenue trend and month-over-month growth rate.</p>

      {isLoading || isError || !hasData ? (
        <NoData loading={isLoading} error={isError} noData={!hasData} />
      ) : (
        <div className='analytics-chart-card__content'>
          <Line data={chartData} options={buildChartOptions()} />
        </div>
      )}
    </Paper>
  );
};

export default ArrGrowthChart;
